import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Firebase
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';

// Components
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Routes/ProtectedRoute';
import AdminRoute from './components/Routes/AdminRoute';

// Pages
import Home from './pages/Home';
import Register from './pages/Auth/Register';
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import RequesterProfile from './pages/Profile/RequesterProfile';
import ProviderProfile from './pages/Profile/ProviderProfile';
import ProjectCreate from './pages/Project/ProjectCreate';
import ProjectList from './pages/Project/ProjectList';
import ProjectDetail from './pages/Project/ProjectDetail';
import MarketplacePage from './pages/Marketplace/MarketplacePage';
import RequesterDashboard from './pages/Dashboard/RequesterDashboard';
import ProviderDashboard from './pages/Dashboard/ProviderDashboard';
import ProviderSubmissions from './pages/Provider/ProviderSubmissions';
import AdvertisingPage from './pages/Advertising/AdvertisingPage';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminUsers from './pages/Admin/AdminUsers';
import AdminCommunication from './pages/Admin/AdminCommunication';
import AdminProfile from './pages/Admin/AdminProfile';
import AdminKPI from './pages/Admin/AdminKPI';
import AdminAdvertising from './pages/Admin/AdminAdvertising';
import AdminSupport from './pages/Admin/AdminSupport';
import AdminMessages from './pages/Admin/AdminMessages';
import AdminProjects from './pages/Admin/AdminProjects';
import BusinessPlan from './pages/BusinessPlan/BusinessPlan';
import NotFound from './pages/NotFound';
import TermsOfService from './pages/Legal/TermsOfService';

// Context
import { AuthProvider } from './context/AuthContext';
import { AlertBannerProvider } from './context/AlertBannerContext';
import { LanguageProvider } from './context/LanguageContext';

function App() {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthenticated(!!user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <LanguageProvider>
        <AlertBannerProvider>
          <Router>
            <ToastContainer position="top-right" autoClose={3000} />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Layout />}>
                <Route index element={<Home />} />
                <Route path="register" element={<Register />} />
                <Route path="login" element={<Login />} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="terms-of-service" element={<TermsOfService />} />

                {/* Protected routes */}
                <Route element={<ProtectedRoute />}>
                  <Route path="business-plan" element={<BusinessPlan />} />
                  <Route path="requester/profile" element={<RequesterProfile />} />
                  <Route path="provider/profile" element={<ProviderProfile />} />
                  <Route path="provider/submissions" element={<ProviderSubmissions />} />
                  <Route path="advertising" element={<AdvertisingPage />} />
                  <Route path="project/create" element={<ProjectCreate />} />
                  <Route path="project/list" element={<ProjectList />} />
                  <Route path="project/:id" element={<ProjectDetail />} />
                  <Route path="marketplace" element={<MarketplacePage />} />
                  <Route path="requester/dashboard" element={<RequesterDashboard />} />
                  <Route path="provider/dashboard" element={<ProviderDashboard />} />
                </Route>

                {/* Admin routes */}
                <Route element={<AdminRoute />}>
                  <Route path="admin/dashboard" element={<AdminDashboard />} />
                  <Route path="admin/users" element={<AdminUsers />} />
                  <Route path="admin/communication" element={<AdminCommunication />} />
                  <Route path="admin/profile" element={<AdminProfile />} />
                  <Route path="admin/kpi" element={<AdminKPI />} />
                  <Route path="admin/advertising" element={<AdminAdvertising />} />
                  <Route path="admin/support" element={<AdminSupport />} />
                  <Route path="admin/messages" element={<AdminMessages />} />
                  <Route path="admin/projects" element={<AdminProjects />} />
                </Route>
              </Route>

              {/* Catch-all route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </AlertBannerProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;